import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useVideoEditor } from '@/hooks/use-video-editor';
import { useVideosList } from '@/hooks/useVideosList';
import { useToast } from '@/hooks/use-toast';
import { videoService } from '@/services/videoService';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';
import { defaultSalesNotification } from '@/store/videoFeatures/types/sales-notification';
import { defaultFinalCountdown } from '@/store/videoFeatures/types/final-countdown';
import { stringToRgba } from '@/components/ColorPickerRGBA';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { toPercentOrNaN } from '@/utils/autoplayOverlayHydration';
import { normalizeHexColor } from '@/lib/colors';
import { actionButtonsFromVideo } from '@/lib/action-button/api-mapping';

const LAST_VIDEO_KEY = 'hostvsl_last_personalization_video';

/**
 * Normaliza uma cor preservando rgba/rgb ou convertendo hex para formato correto
 * Para componentes que aceitam tanto rgba quanto hex (ColorEditorPopover)
 */
const normalizeColor = (color: string | null | undefined, fallback: string = '#000000'): string => {
  if (!color || typeof color !== 'string') return fallback;

  let c = color.trim();

  // Se é rgba ou rgb, manter como está (já é válido)
  if (/^rgba?\s*\(/i.test(c)) {
    return c;
  }

  // Se já está no formato correto #RRGGBB
  if (/^#[0-9A-Fa-f]{6}$/.test(c)) {
    return c.toUpperCase();
  }

  // Se está no formato #RGB, expandir para #RRGGBB
  if (/^#[0-9A-Fa-f]{3}$/.test(c)) {
    return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`.toUpperCase();
  }

  // Se está sem o #, adicionar
  if (/^[0-9A-Fa-f]{6}$/.test(c)) {
    return `#${c}`.toUpperCase();
  }

  if (/^[0-9A-Fa-f]{3}$/.test(c)) {
    return `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`.toUpperCase();
  }

  // Retornar fallback se não reconhecer o formato
  return fallback;
};

// Interface para compatibilidade com o tipo Video esperado
interface Video {
  id: string;
  title: string;
  videoUrl: string;
  status: string;
  [key: string]: any;
}

export function usePersonalization() {
  useLanguage();
  const videoEditor = useVideoEditor();
  const { toast } = useToast();
  const resetFeatures = useVideoFeaturesStore((state) => state.resetFeatures);
  const { user } = useCustomAuth();

  // Usar o hook correto que busca vídeos do backend
  const { videos, isLoading: isLoadingVideos } = useVideosList({
    initialLimit: 1000, // Buscar todos os vídeos disponíveis
    enabled: true
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showVideoLibraryModal, setShowVideoLibraryModal] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Ref para controlar se já carregamos os dados do vídeo atual
  const loadedVideoIdRef = useRef<string | null>(null);

  // Resetar isSaved quando houver mudanças não salvas
  useEffect(() => {
    if (videoEditor.hasUnsavedChanges && videoEditor.isSaved) {
      if (videoEditor.setIsSaved) {
        videoEditor.setIsSaved(false);
      }
    }
  }, [videoEditor.hasUnsavedChanges, videoEditor.isSaved, videoEditor.setIsSaved]);

  // Filter only videos that are not currently being processed and convert to expected format
  const processedVideos: Video[] = (videos || [])
    .filter(v => v.status !== 'processing')
    .map(v => ({
      ...v,
      videoUrl: (v as any).videoDownload || (v as any).videoMP4 || (v as any).url || (v as any).videoUrl || '',
      videoStreamUrl: (v as any).video || '',
    }));
  
  // Auto-load last video on mount (only if no videoId in URL via special signal)
  useEffect(() => {
    // Se ainda está carregando vídeos, mantenha isInitializing true
    if (isLoadingVideos) {
      setIsInitializing(true);
      return;
    }

    if (hasAutoLoaded) return; // Prevent re-runs

    // Marcar como carregado imediatamente se há vídeos disponíveis
    // Isso permite que o vídeo da URL seja carregado sem esperar
    if (processedVideos.length > 0) {
      setHasAutoLoaded(true);
      setIsInitializing(false);

      // Tentar carregar último vídeo apenas se não houver videoId na URL
      // Essa verificação será feita pela página Personalization
      const lastVideoId = localStorage.getItem(LAST_VIDEO_KEY);
      if (lastVideoId && !videoEditor.features.videoUrl) {
        const lastVideo = processedVideos.find(v => v.id === lastVideoId);
        if (lastVideo) {
          loadVideo(lastVideo).catch(err => console.error('Failed to auto-load video:', err));
        }
      }
    } else if (!isLoadingVideos) {
      // Se não está mais carregando mas não há vídeos, sair do modo initializing
      setIsInitializing(false);
      setHasAutoLoaded(true);
    }
  }, [processedVideos.length, isLoadingVideos, hasAutoLoaded]); // Incluir isLoadingVideos nas dependências
  
  // Handle video selection from library
  const handleVideoSelect = async (video: Video) => {
    // Só verifica mudanças não salvas se já houver um vídeo carregado
    const hasVideoLoaded = Boolean(videoEditor.features.videoUrl);

    if (hasVideoLoaded && videoEditor.hasUnsavedChanges) {
      videoEditor.setPendingNavigation(`load-video-${video.id}`);
      videoEditor.setShowUnsavedDialog(true);
      return;
    }

    // Salvar no localStorage
    localStorage.setItem(LAST_VIDEO_KEY, video.id);

    // Resetar estado antes de carregar novo vídeo
    resetFeatures();
    videoEditor.setHasUnsavedChanges(false);

    // Resetar ref para forçar recarregamento quando usuário seleciona novo vídeo
    loadedVideoIdRef.current = null;
    loadVideo(video);
  };
  
  const loadVideo = async (video: Video) => {
    // Se já carregamos este vídeo, não recarregar
    if (loadedVideoIdRef.current === video.id) {
      return;
    }

    setIsLoading(true);

    // Resetar estado do vídeo anterior para evitar mistura de configurações
    resetFeatures();

    try {
      // Buscar dados completos do vídeo do backend
      const response = await videoService.getVideoById(video.id);

      // A resposta já vem como { response: videoData } (axios já extraiu o data)
      const videoData = response?.data?.response || response?.response;

      if (!videoData) {
        console.error('[usePersonalization] No video data in response:', response);
        throw new Error(translateInterfaceText("Dados do vídeo não encontrados"));
      }

      // Exibição por Dispositivo — mapear responsive_config para a store
      // (feito aqui, antes de resetFeatures ou qualquer outra lógica)
      const rawResponsiveConfig = videoData.responsive_config;
      const responsiveConfig = typeof rawResponsiveConfig === 'string'
        ? JSON.parse(rawResponsiveConfig)
        : rawResponsiveConfig;
      if (responsiveConfig) {
        videoEditor.setFeature('responsivePlayer', responsiveConfig);
      }

      // Marcar como carregado
      loadedVideoIdRef.current = video.id;

      // ✅ Verificar se o vídeo tem teste de autoplay ANTES de fazer requisições
      let autoplayTestData = null;

      if (videoData.is_autoplay_test === true && user) {
        try {
          // Importar serviço de autoplay test
          const { autoplayTestService } = await import('@/services/autoplayTestService');

          // Buscar todos os testes do usuário
          const userTests = await autoplayTestService.findUserAutoplayTests(user.id);

          // Encontrar o teste relacionado a este vídeo
          const relatedTest = userTests.find((test: any) =>
            test.id_video === video.id || test.originalVideoId === video.id
          );

          if (relatedTest) {
            // Buscar métricas do teste
            const testId = relatedTest.id_autoplay_test || relatedTest.id;

            // Calcular datas (últimos 30 dias)
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 30);

            const inicio = startDate.toISOString().split('T')[0];
            const fim = endDate.toISOString().split('T')[0];

            const metricsResponse = await autoplayTestService.getAutoplayTestMetrics(testId, inicio, fim);

            autoplayTestData = {
              testId,
              metrics: metricsResponse
            };
          } else {
            console.warn('⚠️ Teste de autoplay não encontrado para este vídeo');
          }
        } catch (error) {
          console.error('❌ Erro ao buscar teste de autoplay:', error);
        }
      }
      // Se não tem teste de autoplay, autoplayTestData permanece null e continuamos normalmente

      // Preencher TODOS os campos diretamente no store (igual ao projeto antigo)
      // Basic video info
      videoEditor.setFeature('videoUrl', videoData.videoDownload || videoData.videoMP4 || video.videoUrl);
      videoEditor.setFeature('videoStreamUrl', videoData.video || '');
      videoEditor.setFeature('videoId', videoData.id_video);
      videoEditor.setFeature('title', videoData.nomeVideo);
      videoEditor.setFeature('videoFrame', videoData.frame || videoData.thumb || videoData.thumbnailUrl || videoData.thumbnail_url || videoData.imagemPause || videoData.imagemInicial || videoData.imageUrl || '');
      videoEditor.setFeature('isAutoplayTest', videoData.is_autoplay_test || false);

      // Controles
      videoEditor.setFeature('showControls', videoData.haveControls || false);
      videoEditor.setFeature('showProgressBar', videoData.haveProgresso || false);
      videoEditor.setFeature('skipForward', videoData.haveSkip || false);
      videoEditor.setFeature('rewind', videoData.haveRewind || false);
      videoEditor.setFeature('volumeControl', videoData.haveVolume || false);
      videoEditor.setFeature('fullScreen', videoData.haveFullScreen || false);
      videoEditor.setFeature('playbackSpeedControl', videoData.havePlaybackSpeed || false);
      videoEditor.setFeature('pictureInPicture', videoData.havePictureInPicture || false);
      videoEditor.setFeature('tvCastEnabled', videoData.haveTvCast || false);
      videoEditor.setFeature('storyboardEnabled', videoData.haveStoryboard || false);
      videoEditor.setFeature('storyboardUrl', videoData.storyboardUrl || undefined);
      videoEditor.setFeature('showCurrentTime', videoData.haveTempoAtual || false);
      videoEditor.setFeature('bigPlayButton', videoData.haveBigPlay || false);
      videoEditor.setFeature('smallPlayButton', videoData.haveSmallPlay || false);
      videoEditor.setFeature('disableControls', videoData.noneControlsMoreAutoplay || false);

      // Control colors (novo sistema)
      const controlColor = videoData.controlColor || 'rgba(135, 145, 163, 0.45)';
      const controlColorIcon = videoData.controlColorIcon || 'rgba(255, 255, 255, 1)';

      videoEditor.setFeature('controlColor', controlColor);
      videoEditor.setFeature('controlColorIcon', controlColorIcon);

      // Barra de controles (toggle + cor de fundo da barra)
      videoEditor.setFeature('controlBarEnabled', videoData.controlBarEnabled ?? false);
      videoEditor.setFeature('controlBarColor', videoData.controlBarColor || controlColor);

      // Tema dos controles. Normaliza o id legado 'custom' (upload de ícone,
      // descontinuado) para 'safari' — vídeos antigos migram ao re-salvar.
      const loadedControlsTheme =
        videoData.controlsTheme === 'custom' ? 'safari' : videoData.controlsTheme || 'minimal';
      videoEditor.setFeature('controlsTheme', loadedControlsTheme);
      videoEditor.setFeature('customIconUrl', videoData.customIconUrl || null);

      // Converter controlColor (rgba) para playerColors (backgroundColor + opacity)
      const rgba = stringToRgba(controlColor);
      const hex = `#${rgba.r.toString(16).padStart(2, '0')}${rgba.g.toString(16).padStart(2, '0')}${rgba.b.toString(16).padStart(2, '0')}`;

      videoEditor.setFeature('playerColors', {
        primary: '#FFFFFF',
        secondary: '#64748B',
        backgroundColor: hex,
        borderRadius: videoData.borderRadius || 4,
        opacity: rgba.a
      });

      // Legacy controlsColor (para compatibilidade)
      if (videoData.controlColor) {
        videoEditor.setFeature('controlsColor', normalizeHexColor(videoData.controlColor, '#FFFFFF'));
      }

      // Smart Pause
      videoEditor.setFeature('smartPause', videoData.haveSmartPause || false);

      // Bloqueio de pause
      videoEditor.setFeature('blockPause', videoData.haveBlockPause || false);

      // Recomeçar após o fim
      videoEditor.setFeature('haveRestart', videoData.haveRestart || false);

      // Border (ColorPicker usa hex)
      videoEditor.setFeature('border', {
        enabled: videoData.haveBorder || false,
        color: normalizeHexColor(videoData.borderColor, '#FFFFFF'),
        width: videoData.borderWidth || 2,
        radius: videoData.borderRadius ?? (videoData.haveBorderRadius ? 12 : 0),
        secondaryColor: '#FFFFFF',
        stylePreset: 'none',
      });

      // Autoplay Overlay (ColorEditorPopover aceita rgba)
      let templateId = videoData.typesAutoplay || 'autoPlay';
      if (videoData.haveSmallTemplate) templateId = 'small';

      videoEditor.setFeature('autoPlay', videoData.haveAutoPlay || false);

      const autoplayOverlayConfig = {
        enabled: videoData.haveAutoPlay || false,
        textTop: videoData.textSuperior || '',
        textBottom: videoData.textInferior || '',
        colors: {
          background: normalizeColor(videoData.cor, 'rgba(0, 170, 255, 0.46)'),
          text: normalizeColor(videoData.corText, '#FFFFFF'),
          icon: normalizeColor(videoData.autoplayIconColor || videoData.corText, '#FFFFFF'),
          border: '#FFFFFF'
        },
        whiteBorder: videoData.haveBorderWhite || false,
        pulseAnimation: videoData.havePulse || false,
        pulseEffect: videoData.havePulse || false,
        customImage: videoData.customImage || undefined,
        useTemplate: !videoData.haveCustomImage,
        showIcon: true,
        templateId: templateId,
        autoRestart: videoData.haveRestart || false,
        fontFamily: 'Inter',
        fontSize: '16px',
        fontWeight: '400',
        roundedCorners: true,
        showBorder: videoData.haveBorderWhite || false,
        autoplayType: 'same-video',
        clipStart: 0,
        clipEnd: undefined,
        backgroundStyle: 'darkened',
        backgroundIntensity: 65,
        iconType: 'sound',
        abTestConfig: autoplayTestData ? {
          enabled: true,
          variations: [], // TODO: Carregar variações reais do backend
          selectedTemplates: [],
          trafficDistribution: {},
          variantTrafficDistribution: {},
          status: 'active',
          isRealTimeEnabled: true,
          lastUpdated: new Date().toISOString(),
          testId: autoplayTestData.testId,
        } : {
          enabled: false,
          variations: [],
          selectedTemplates: [],
          trafficDistribution: {},
          variantTrafficDistribution: {},
          status: 'draft',
          isRealTimeEnabled: false,
          lastUpdated: ''
        },
        // Hidrata posição/tamanho custom do overlay. toPercentOrNaN tolera
        // strings numéricas (Sequelize com FLOAT/DECIMAL às vezes retorna string
        // dependendo do driver MySQL) e trata `null` como ausência — Number(null)
        // seria 0 e fabricaria uma geometria falsa (ver o util).
        position: (() => {
          const x = toPercentOrNaN(videoData.autoplayPositionXPercent);
          const y = toPercentOrNaN(videoData.autoplayPositionYPercent);
          return Number.isFinite(x) && Number.isFinite(y)
            ? { xPercent: x, yPercent: y }
            : undefined;
        })(),
        size: (() => {
          const w = toPercentOrNaN(videoData.autoplayWidthPercent);
          if (!Number.isFinite(w)) return undefined;
          const h = toPercentOrNaN(videoData.autoplayHeightPercent);
          return {
            widthPercent: w,
            heightMode: 'auto' as const,
            heightPercent: Number.isFinite(h) ? h : undefined,
          };
        })(),
        detectInteraction: videoData.autoplayDetectInteraction === true,
        autoClick: videoData.autoplayAutoClick === true,
        // Tela flutuante (PiP) do autoplay — coluna própria, sem ambiguidade
        // com o PiP dos controles (`havePictureInPicture`).
        havePictureInPicture: videoData.autoplayPictureInPicture === true,
        // Tela cheia do autoplay — a coluna `haveFullScreen` é COMPARTILHADA
        // com o fullscreen dos controles, então um `true` só pode ser atribuído
        // ao autoplay quando os controles estão desligados (com controles
        // ligados o `true` pode ter vindo do botão da barra de controles).
        haveFullScreen: videoData.haveFullScreen === true && !videoData.haveControls,
      };

      videoEditor.setFeature('autoplayOverlay', autoplayOverlayConfig as any);

      // Continue Watching
      videoEditor.setFeature('continueWatching', {
        enabled: videoData.haveContinue || false,
        threshold: 5,
        expires: 7,
        message: videoData.textMessage || "Você parou em um ponto importante do vídeo.",
        buttonText: videoData.textContinue || 'Continuar agora',
        restartText: videoData.textRestart || 'Reiniciar vídeo'
      });

      videoEditor.setFeature('continueWatchingOverlay', {
        backgroundColor: normalizeHexColor(videoData.backgroundContinue, '#0E0E0E'),
        textColor: normalizeHexColor(videoData.continueTextColor, '#FFFFFF'),
        playButtonBgColor: videoData.continuePlayButtonBgColor || '',
        restartButtonBgColor: videoData.continueRestartButtonBgColor || '',
        buttonColor: '#1D4FFF',
        buttonTextColor: '#FFFFFF',
        blurBackground: true,
        showTime: true,
        position: 'center',
        enabled: videoData.haveContinue || false,
        title: "Retomar reprodução",
        message: videoData.textMessage || '',
        continueText: videoData.textContinue || '',
        restartText: videoData.textRestart || '',
        overlayOpacity: 75,
        animationEntrance: 'fade',
        size: 'small',
        template: videoData.typesContinue === '' ? 'continue' : (videoData.typesContinue || 'template1'),
        useTemplate: true,
        autoResume: { enabled: false, delay: 5, showCountdown: true },
        soundAlert: { enabled: false, type: 'click' }
      });

      // Audience (Ao Vivo)
      videoEditor.setFeature('audience', {
        enabled: videoData.haveAoVivo || false,
        viewersCount: videoData.simulationAoVivo ? parseInt(videoData.simulationAoVivo) || undefined : undefined,
        speed: (videoData.audienceSimulationSpeed as any) || 'medium',
        message: videoData.textAoVivo || '',
        style: {
          // Mantém o valor cru (rgba) para preservar a opacidade (canal alpha) da caixa
          // do Ao Vivo; normalizeHexColor converteria para #RRGGBB e descartaria o alpha.
          textColor: videoData.fontColorAoVivo || '#FFFFFF',
          backgroundColor: videoData.backgroundAoVivo || '#000000',
          useTransparency: true,
          opacity: 70
        }
      });

      // Normaliza tamanho vindo do backend (number, string numérica ou enum
      // legado) para number em px. Tolerante a dados antigos não migrados.
      const LEGACY_SIZE_MAP: Record<string, number> = {
        small: 16, medium: 24, large: 32, xlarge: 40,
        pequeno: 16, medio: 24, grande: 32,
      };
      const resolveSize = (raw: any, defaultPx: number): number => {
        if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
        if (typeof raw === 'string') {
          const num = parseInt(raw, 10);
          if (!isNaN(num) && num > 0) return num;
          const legacy = LEGACY_SIZE_MAP[raw.trim().toLowerCase()];
          if (legacy) return legacy;
        }
        return defaultPx;
      };

      videoEditor.setFeature('headline', {
        enabled: videoData.haveHeadline || false,
        name: '',
        headline: {
          text: videoData.headlineText || '',
          size: resolveSize(videoData.headlineSize, 24),
          fontFamily: (videoData.headlineTypeFont as 'Inter' | 'Arial' | 'sans-serif') || 'Inter',
          fontWeight: videoData.headlineFontWeight === 'bold' ? 'bold' : 'normal',
          color: normalizeHexColor(videoData.headlineColor, '#FFFFFF')
        },
        subheadline: {
          text: videoData.subHeadlineText || '',
          size: resolveSize(videoData.subHeadlineSize, 16),
          fontFamily: (videoData.headlineTypeFont as 'Inter' | 'Arial' | 'sans-serif') || 'Inter',
          fontWeight: 'normal',
          color: normalizeHexColor(videoData.subHeadlineColor, '#E5E5E5')
        },
        textMode: { enabled: !videoData.isHTMLSelected },
        htmlMode: { enabled: videoData.isHTMLSelected || false, code: videoData.headlineHtmlCode || '' },
        imageMode: { enabled: false },
        timing: {
          start: videoData.headlineInitialDelay || '',
          end: videoData.headlineEndDelay || '',
          showThroughout: videoData.headlineShowThroughout ?? true,
        },
        position: 'bottom-center',
        offsetX: 0,
        offsetY: 0,
        background: {
          enabled: true,
          type: 'solid',
          color: 'rgba(0, 0, 0, 0.7)',
          opacity: 0.7,
          borderRadius: 8,
          padding: 16
        },
        animation: { enabled: true, type: 'fade', duration: 0.5, delay: 0 },
        abTestConfig: { enabled: false, variations: [], status: 'draft', trafficSplit: 50 }
      });

      // Logo
      if (videoData.logoImg) {
        const logoPositionMap: Record<string, any> = {
          'S_Esquerda': 'top-left', 'S_Meio': 'top-center', 'S_Direita': 'top-right',
          'I_Esquerda': 'bottom-left', 'I_Meio': 'bottom-center', 'I_Direita': 'bottom-right',
        };
        videoEditor.setFeature('logo', {
          enabled: true,
          url: videoData.logoImg,
          imageUrl: videoData.logoImg,
          linkUrl: videoData.urlLogo || '',
          position: logoPositionMap[videoData.selectedLogoPosition] || 'top-left',
          size: videoData.logoSize || 10,
          opacity: parseInt(videoData.transparencyLogo) || 100
        });
      } else {
        videoEditor.setFeature('logo', undefined);
      }

      // Botões de ação (lista)
      videoEditor.setFeature('actionButtons', actionButtonsFromVideo(videoData));

      // Thumbnails
      // Âncoras do backend ("S_Meio") → posição do botão da thumbnail de pausa.
      const positionMap: Record<string, any> = {
        'S_Esquerda': 'top-left', 'S_Meio': 'top-center', 'S_Direita': 'top-right',
        'M_Esquerda': 'center-left', 'M_Meio': 'center', 'M_Direita': 'center-right',
        'I_Esquerda': 'bottom-left', 'I_Meio': 'bottom-center', 'I_Direita': 'bottom-right',
      };
      if (videoData.thumb) {
        videoEditor.setFeature('pauseThumbnail', {
          enabled: true,
          imageUrl: videoData.thumb,
          pauseMode: (videoData.thumbPauseMode as 'always' | 'timeRange') || 'always',
          timeRange: { start: videoData.thumbPauseTimeStart || '' },
          actionButton: videoData.haveButtonThumb ? {
            text: videoData.textButton || '',
            link: videoData.textLink || '',
            size: (videoData.selectedSize as 'small' | 'medium' | 'large') || 'medium',
            position: positionMap[videoData.selectedPosition] || 'center',
            colors: {
              background: normalizeHexColor(videoData.backgroundButton, '#3B82F6'),
              text: normalizeHexColor(videoData.textButtonColor, '#FFFFFF'),
              hover: normalizeHexColor(videoData.backgroundButtonHover, '#2563EB'),
              hoverText: normalizeHexColor(videoData.textButtonColorHover, '#FFFFFF')
            }
          } : undefined
        });
      } else {
        videoEditor.setFeature('pauseThumbnail', undefined);
      }

      if (videoData.thumbInicio) {
        videoEditor.setFeature('initialThumbnail', { enabled: true, imageUrl: videoData.thumbInicio });
      } else {
        videoEditor.setFeature('initialThumbnail', undefined);
      }

      if (videoData.thumbFinal) {
        videoEditor.setFeature('endThumbnail', { enabled: true, imageUrl: videoData.thumbFinal });
      } else {
        videoEditor.setFeature('endThumbnail', undefined);
      }

      // Playback Speed (Manual)
      if (videoData.haveTurbo) {
        videoEditor.setFeature('playbackSpeed', videoData.turboVelocity || 1);
      }

      // Speed Test (Teste Automático)
      if (videoData.haveTurboTest && user) {
        try {
          const { speedTestService } = await import('@/services/speedTestService');
          const userTests = await speedTestService.findSpeedTestsByUserId(user.id);

          // Encontrar teste ativo para este vídeo
          const activeTestForVideo = userTests.find((test: any) =>
            test.id_video === videoData.id_video
          );

          if (activeTestForVideo) {
            // Parse speeds (vem como string JSON do backend)
            let speedsArray: number[] = [];
            try {
              speedsArray = typeof activeTestForVideo.speeds === 'string'
                ? JSON.parse(activeTestForVideo.speeds)
                : activeTestForVideo.speeds || [];
            } catch (error) {
              speedsArray = [1.0, 1.5];
            }

            // Criar objeto de teste no formato do frontend
            const speedTest = {
              id: activeTestForVideo.id_speed_test || activeTestForVideo.id,
              videoId: videoData.id_video,
              userId: user.id,
              velocidade: 1.0,
              speeds: speedsArray,
              date: activeTestForVideo.created_at || new Date().toISOString(),
              enabled: true,
              isAutomatic: true,
              velocidadeInicial: speedsArray[0] || 1.0,
              velocidadeFinal: speedsArray[speedsArray.length - 1] || 1.5,
              testStatus: 'running' as const,
              createdAt: activeTestForVideo.created_at,
              updatedAt: activeTestForVideo.updated_at,
            };

            // Adicionar teste ao state
            videoEditor.setFeature('speedTests', [speedTest]);
          }
        } catch (error) {
          console.error('Error loading speed test:', error);
        }
      }

      // Progress Bar
      if (videoData.corBar) {
        videoEditor.setFeature('progressBarColor', normalizeHexColor(videoData.corBar, '#3B82F6'));
      }

      // Progress Bar (Fake Bar) - carregar estado enabled do backend
      if (videoData.haveFakeBar !== undefined) {
        const fakeBarHeight = videoData.fakeBarHeight || 12;
        videoEditor.setFeature('progressBar', {
          enabled: videoData.haveFakeBar,
          color: videoData.corBar || '#3B82F6',
          height: fakeBarHeight,
          showOnHover: false,
          animated: false
        });
        videoEditor.setFeature('progressBarHeight', fakeBarHeight);
      }

      // Legendas (MySQL salva como JSON string, MongoDB já é objeto/array)
      const parseJsonField = (value: any) => {
        if (typeof value !== 'string') return value;
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        } catch { return value; }
      };
      if (videoData.subtitles !== undefined && videoData.subtitles !== null) {
        videoEditor.setFeature('subtitles', parseJsonField(videoData.subtitles) || []);
      }
      if (videoData.subtitleConfig !== undefined && videoData.subtitleConfig !== null) {
        videoEditor.setFeature('subtitleConfig', parseJsonField(videoData.subtitleConfig));
      }

      // Mini Hooks (armazenado como TEXT JSON no MySQL)
      if (videoData.miniHooks !== undefined && videoData.miniHooks !== null) {
        const parsed = parseJsonField(videoData.miniHooks);
        if (parsed && typeof parsed === 'object') {
          videoEditor.setFeature('miniHooks', parsed);
        }
      }

      // Jornada de Conversão (armazenado como JSON no MySQL)
      if (videoData.conversionJourney !== undefined && videoData.conversionJourney !== null) {
        const parsed = parseJsonField(videoData.conversionJourney);
        if (parsed && typeof parsed === 'object') {
          videoEditor.setFeature('conversionJourney', parsed);
        }
      }

      // Feedback do Vídeo (haveVideoFeedback + videoFeedbackConfig no Mongo).
      // Hidrata SEMPRE (mesmo sem config) para não vazar, via persist, o estado
      // do vídeo aberto anteriormente. Converte o formato persistido (player)
      // para o formato do editor (VideoFeedback).
      const { fromPlayerConfig } = await import('@/services/videoFeedbackService');
      const parsedFeedbackCfg =
        videoData.videoFeedbackConfig !== undefined && videoData.videoFeedbackConfig !== null
          ? parseJsonField(videoData.videoFeedbackConfig)
          : null;
      videoEditor.setFeature(
        'videoFeedback',
        fromPlayerConfig(!!videoData.haveVideoFeedback, parsedFeedbackCfg)
      );

      // Notificação de Vendas e Contagem Final (objetos JSON no Mongo).
      // Hidratar aqui também é obrigatório: carregar em só um dos dois hooks
      // deixa o outro com a config do vídeo aberto anteriormente (o store tem
      // `persist`). Hidrata SEMPRE, caindo no default quando o vídeo não tem
      // config salva.
      const parsedSalesNotification =
        videoData.salesNotification !== undefined && videoData.salesNotification !== null
          ? parseJsonField(videoData.salesNotification)
          : null;
      videoEditor.setFeature(
        'salesNotification',
        parsedSalesNotification && typeof parsedSalesNotification === 'object'
          ? parsedSalesNotification
          : defaultSalesNotification
      );

      const parsedFinalCountdown =
        videoData.finalCountdown !== undefined && videoData.finalCountdown !== null
          ? parseJsonField(videoData.finalCountdown)
          : null;
      videoEditor.setFeature(
        'finalCountdown',
        parsedFinalCountdown && typeof parsedFinalCountdown === 'object'
          ? parsedFinalCountdown
          : defaultFinalCountdown
      );

      // Save last selected video
      localStorage.setItem(LAST_VIDEO_KEY, video.id);

      // Reset unsaved changes
      videoEditor.setHasUnsavedChanges(false);

      toast({
        title: translateInterfaceText("Vídeo carregado"),
        description: translateInterfaceText("\"{value0}\" está pronto para personalização", { value0: (videoData.nomeVideo) })
      });
    } catch (error) {
      console.error('Error loading video:', error);
      toast({
        title: translateInterfaceText("Falha ao carregar vídeo"),
        description: translateInterfaceText("Ocorreu um erro ao carregar o vídeo"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle video selection by ID (for VideoSelectorV2)
  const handleVideoSelectById = (videoId: string) => {
    const video = processedVideos.find(v => v.id === videoId);
    
    if (!video) {
      toast({
        title: translateInterfaceText("Vídeo não encontrado"),
        description: translateInterfaceText("O vídeo selecionado não está disponível"),
        variant: "destructive"
      });
      return;
    }
    
    handleVideoSelect(video);
  };

  // Override confirmation navigation to handle loading videos
  const confirmNavigation = async () => {
    if (videoEditor.pendingNavigation) {
      if (videoEditor.pendingNavigation.startsWith('load-video-')) {
        // Resetar o estado antes de carregar novo vídeo
        resetFeatures();
        videoEditor.setHasUnsavedChanges(false);

        // Extrair ID do vídeo e carregar
        const videoId = videoEditor.pendingNavigation.replace('load-video-', '');
        const video = processedVideos.find(v => v.id === videoId);
        if (video) {
          // Reset ref to force reload
          loadedVideoIdRef.current = null;
          await loadVideo(video);
        }
      } else if (videoEditor.pendingNavigation === 'load-video') {
        // Re-trigger modal
        setShowVideoLibraryModal(true);
      } else {
        // Handle regular navigation
        videoEditor.confirmNavigation();
      }
      videoEditor.setShowUnsavedDialog(false);
      videoEditor.setPendingNavigation(null);
      videoEditor.setHasUnsavedChanges(false);
    }
  };

  // Handle save - salva todas as configurações no backend
  const handleSave = async () => {
    const features = videoEditor.features;
    const videoId = features.videoId;

    if (!videoId) {
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: translateInterfaceText("Nenhum vídeo selecionado para salvar"),
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);

    try {
      // Mapeamento de posições frontend -> backend
      const positionToBackend: Record<string, string> = {
        'top-left': 'S_Esquerda',
        'top-center': 'S_Meio',
        'top-right': 'S_Direita',
        'center-left': 'M_Esquerda',
        'center': 'M_Meio',
        'center-right': 'M_Direita',
        'bottom-left': 'I_Esquerda',
        'bottom-center': 'I_Meio',
        'bottom-right': 'I_Direita',
      };

      // Normaliza o tamanho da headline vindo do state (pode ser number do
      // tipo novo ou string/enum legado) para number em px. Após a migration,
      // o backend aceita INT direto.
      const HEADLINE_LEGACY_MAP: Record<string, number> = {
        small: 16, medium: 24, large: 32, xlarge: 40,
        pequeno: 16, medio: 24, grande: 32,
      };
      const resolveHeadlineSize = (raw: any, defaultPx: number): number => {
        if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
        if (typeof raw === 'string') {
          const num = parseInt(raw, 10);
          if (!isNaN(num) && num > 0) return num;
          const legacy = HEADLINE_LEGACY_MAP[raw.trim().toLowerCase()];
          if (legacy) return legacy;
        }
        return defaultPx;
      };

      // Construir objeto com TODOS os campos para o backend
      const dataToSave: Record<string, any> = {
        // === AUTOPLAY ===
        haveAutoplay: features.autoplayOverlay?.enabled || features.autoPlay || false,
        cor: features.autoplayOverlay?.colors?.background || 'rgba(0, 170, 255, 0.46)',
        corText: features.autoplayOverlay?.colors?.text || '#FFFFFF',
        textSuperior: features.autoplayOverlay?.textTop || '',
        textInferior: features.autoplayOverlay?.textBottom || '',
        typesAutoplay: features.autoplayOverlay?.templateId || 'autoPlay',
        autoplayIconColor: features.autoplayOverlay?.colors?.icon || '#FFFFFF',
        haveSmallTemplate: features.autoplayOverlay?.templateId === 'small',
        // Salva só o primário. O legado (`pulseEffect`/`showBorder`) é duplicado
        // no load do backend, mas só o primário é atualizado pelos switches —
        // se combinássemos com OR aqui, o toggle do usuário seria perdido (o
        // legado travaria o valor em `true` para sempre).
        havePulse: features.autoplayOverlay?.pulseAnimation || false,
        haveRestart: features.haveRestart ?? false,
        haveBorderWhite: features.autoplayOverlay?.whiteBorder || false,
        // Posição/tamanho custom do overlay de autoplay (templates
        // autoPlay/small/large/transparent). Persistidos em % do container
        // do vídeo; lidos pelo runner.js do playerjs em produção.
        autoplayPositionXPercent: features.autoplayOverlay?.position?.xPercent ?? null,
        autoplayPositionYPercent: features.autoplayOverlay?.position?.yPercent ?? null,
        autoplayWidthPercent: features.autoplayOverlay?.size?.widthPercent ?? null,
        autoplayHeightPercent: features.autoplayOverlay?.size?.heightPercent ?? null,
        autoplayDetectInteraction: features.autoplayOverlay?.detectInteraction === true,
        autoplayAutoClick: features.autoplayOverlay?.autoClick === true,
        // Tela flutuante (PiP) do autoplay — coluna dedicada, NÃO combinar com
        // o PiP dos controles (`havePictureInPicture`) para não vazar o botão
        // de um recurso para o outro.
        autoplayPictureInPicture: features.autoplayOverlay?.havePictureInPicture === true,
        haveCustomImage: features.autoplayOverlay?.customImage ? true : false,
        customImage: features.autoplayOverlay?.customImage || null,

        // === SMART PAUSE ===
        haveSmartPause: features.smartPause ?? false,

        // === BLOQUEIO DE PAUSE === (não depende de controles — vale com autoplay/sem controles)
        haveBlockPause: features.blockPause ?? false,

        // === BORDER ===
        haveBorder: features.border?.enabled || false,
        borderColor: features.border?.color || '#ffffff',
        borderWidth: features.border?.width || 2,
        borderRadius: features.border?.radius ?? 0,

        // === CONTROLS ===
        // Se controles não estão ativos, reset completo para não enviar dados sujos ao backend
        haveControls: features.showControls || false,
        haveBigPlay: features.showControls ? (features.bigPlayButton || false) : false,
        haveProgresso: features.showControls ? (features.showProgressBar || false) : false,
        haveSkip: features.showControls ? (features.skipForward || false) : false,
        haveRewind: features.showControls ? (features.rewind || false) : false,
        haveVolume: features.showControls ? (features.volumeControl || false) : false,
        // O mesmo campo `haveFullScreen` é compartilhado por dois recursos
        // (fullscreen nos controles e fullscreen no autoplay). Enviamos true
        // se qualquer um estiver ativo, pra não sobrescrever o outro.
        haveFullScreen: !!(
          (features.showControls && features.fullScreen) ||
          features.autoplayOverlay?.haveFullScreen
        ),
        havePlaybackSpeed: features.showControls ? (features.playbackSpeedControl || false) : false,
        havePictureInPicture: features.showControls ? (features.pictureInPicture || false) : false,
        // TV Cast depende dos controles reais — o botão fica na barra de controles.
        haveTvCast: features.showControls ? (features.tvCastEnabled || false) : false,
        // Storyboard depende dos controles reais (não funciona com fake bar — barra fake
        // é mutuamente exclusiva com showControls).
        haveStoryboard: features.showControls ? (features.storyboardEnabled || false) : false,
        haveTempoAtual: features.showControls ? (features.showCurrentTime || false) : false,
        haveSmallPlay: features.showControls ? (features.smallPlayButton || false) : false,
        controlColor: features.showControls ? (features.controlColor || '#0129BB') : '#0129BB',
        controlColorIcon: features.showControls ? (features.controlColorIcon || 'rgba(255, 255, 255, 1)') : '#ffffff',

        // === BARRA DE CONTROLES (tema do player) ===
        controlBarEnabled: features.controlBarEnabled ?? false,
        controlBarColor: features.controlBarColor || 'rgba(135, 145, 163, 0.45)',

        // === TEMA DOS CONTROLES (afeta TODOS os ícones) ===
        controlsTheme: features.controlsTheme || 'minimal',
        customIconUrl: features.customIconUrl || null,

        // === PROGRESS BAR ===
        corBarra: features.progressBarColor || features.progressBar?.color || '#3B82F6',
        haveFakeBar: features.progressBar?.enabled || false,
        fakeBarHeight: features.progressBar?.height || features.progressBarHeight || 4,

        // === AUDIENCE (AO VIVO) ===
        haveAoVivo: features.audience?.enabled || false,
        backgroundAoVivo: features.audience?.style?.backgroundColor || '#000000',
        textAoVivo: features.audience?.message || '',
        simulationAoVivo: features.audience?.viewersCount != null ? String(features.audience.viewersCount) : '',
        audienceSimulationSpeed: features.audience?.speed || 'medium',
        fontColorAoVivo: features.audience?.style?.textColor || '#ffffff',

        // === CONTINUE WATCHING ===
        haveContinue: features.continueWatching?.enabled ?? false,
        textContinue: features.continueWatchingOverlay?.continueText || 'Continuar assistindo?',
        textMessage: features.continueWatchingOverlay?.message || 'Você já começou a assistir esse vídeo',
        textRestart: features.continueWatchingOverlay?.restartText || 'Assistir do início',
        backgroundContinue: features.continueWatchingOverlay?.backgroundColor || '#0E0E0E',
        continueTextColor: features.continueWatchingOverlay?.textColor || '#FFFFFF',
        continuePlayButtonBgColor: features.continueWatchingOverlay?.playButtonBgColor || '',
        continueRestartButtonBgColor: features.continueWatchingOverlay?.restartButtonBgColor || '',
        typesContinue: features.continueWatchingOverlay?.template === 'continue' ? '' : (features.continueWatchingOverlay?.template || 'template1'),

        // === HEADLINE ===
        haveHeadline: features.headline?.enabled || false,
        headlineText: features.headline?.headline?.text || '',
        subHeadlineText: features.headline?.subheadline?.text || '',
        headlineColor: features.headline?.headline?.color || '#ffffff',
        subHeadlineColor: features.headline?.subheadline?.color || '#e5e5e5',
        headlineSize: resolveHeadlineSize(features.headline?.headline?.size, 24),
        subHeadlineSize: resolveHeadlineSize(features.headline?.subheadline?.size, 16),
        headlineTypeFont: features.headline?.headline?.fontFamily || 'Inter',
        headlineFontWeight: features.headline?.headline?.fontWeight || 'normal',
        isHTMLSelected: features.headline?.htmlMode?.enabled || false,
        headlineHtmlCode: features.headline?.htmlMode?.code || '',
        headlineInitialDelay: features.headline?.timing?.start || '',
        headlineEndDelay: features.headline?.timing?.end || '',
        headlineShowThroughout: features.headline?.timing?.showThroughout ?? true,

        // === THUMBNAIL BUTTON (na pausa) ===
        haveButtonThumb: features.pauseThumbnail?.actionButton ? true : false,
        selectedSize: features.pauseThumbnail?.actionButton?.size || 'medium',
        textButton: features.pauseThumbnail?.actionButton?.text || '',
        textLink: features.pauseThumbnail?.actionButton?.link || '',
        backgroundButton: features.pauseThumbnail?.actionButton?.colors?.background || '#3b82f6',
        backgroundButtonHover: features.pauseThumbnail?.actionButton?.colors?.hover || '#2563eb',
        textButtonColor: features.pauseThumbnail?.actionButton?.colors?.text || '#ffffff',
        textButtonColorHover: features.pauseThumbnail?.actionButton?.colors?.hoverText || '#ffffff',
        selectedPosition: positionToBackend[features.pauseThumbnail?.actionButton?.position || 'bottom-center'] || 'I_Meio',
        havePause: features.pauseThumbnail?.enabled || false,
        thumbPauseMode: features.pauseThumbnail?.pauseMode || 'always',
        thumbPauseTimeStart: features.pauseThumbnail?.timeRange?.start || '',

        // === MISC ===
        noneControlsMoreAutoplay: features.disableControls || false,
      };

      // Upload da imagem customizada do autoplay (binário → Bunny CDN).
      // Tem que rodar ANTES do saveAllConfigurations, senão a blob URL local
      // sobrescreveria a URL real do Bunny.
      const autoplayFile = useVideoFeaturesStore.getState().tempFiles.autoplayCustomImageFile;
      if (autoplayFile) {
        const formData = new FormData();
        formData.append('customImage', autoplayFile);
        const uploadResp = await videoService.updateAutoplayCustomImage(videoId, formData);
        const finalUrl = uploadResp?.customImage;
        if (finalUrl) {
          dataToSave.customImage = finalUrl;
          dataToSave.haveCustomImage = true;
          videoEditor.setFeature('autoplayOverlay', {
            ...features.autoplayOverlay,
            customImage: finalUrl,
          });
        }
        useVideoFeaturesStore.getState().clearTempFile('autoplayCustomImageFile');
      } else if (typeof dataToSave.customImage === 'string' && dataToSave.customImage.startsWith('blob:')) {
        dataToSave.customImage = null;
      }

      // Chamar a API para salvar todas as configurações principais
      await videoService.saveAllConfigurations(videoId, dataToSave);

      // Chamar a API de turbo/speed separadamente (tem rota própria)
      if (features.playbackSpeed && features.playbackSpeed !== 1) {
        await videoService.updateSpeedTest(videoId, {
          haveTurbo: true,
          turboVelocity: features.playbackSpeed,
        });
        toast({
            title: translateInterfaceText("Velocidade atualizada"),
            description: translateInterfaceText("As configurações de velocidade foram salvas com sucesso"),
          });
      }

      // Marcar como salvo no store
      await useVideoFeaturesStore.getState().saveFeatures();
      videoEditor.setHasUnsavedChanges(false);

      // Marcar como salvo para mostrar o botão "Copiar código"
      if (videoEditor.setIsSaved) {
        videoEditor.setIsSaved(true);
      }

      toast({
        title: translateInterfaceText("Alterações salvas com sucesso"),
        description: translateInterfaceText("Todas as configurações do seu vídeo foram salvas"),
      });
    } catch (error: any) {
      console.error('Error saving video configurations:', error);
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: error?.response?.data?.message || error?.message || translateInterfaceText("Ocorreu um erro ao salvar as configurações. Tente novamente."),
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Usar useMemo para garantir reatividade quando features mudar
  return useMemo(() => ({
    ...videoEditor,
    isLoading,
    isInitializing,
    hasAutoLoaded,
    handleVideoSelect,
    handleVideoSelectById,
    processedVideos,
    showVideoLibraryModal,
    setShowVideoLibraryModal,
    confirmNavigation,
    handleSave,
    isSaving,
  }), [
    videoEditor,
    videoEditor.features, // ← IMPORTANTE: adicionar features nas dependências
    isLoading,
    isInitializing,
    hasAutoLoaded,
    handleVideoSelect,
    handleVideoSelectById,
    processedVideos,
    showVideoLibraryModal,
    setShowVideoLibraryModal,
    confirmNavigation,
    handleSave,
    isSaving,
  ]);
}